import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Sparkles, Wind, Zap, Flame } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POWERS = [
  { id: "slow",   label: "Suave",  icon: Wind,  color: "text-cyan-300",   bg: "border-cyan-400/40 bg-cyan-500/10",   active: "border-cyan-300 bg-cyan-500/40" },
  { id: "normal", label: "Normal", icon: Zap,   color: "text-yellow-300", bg: "border-yellow-400/40 bg-yellow-500/10", active: "border-yellow-300 bg-yellow-500/40" },
  { id: "strong", label: "Fuerte", icon: Flame, color: "text-orange-400", bg: "border-orange-500/40 bg-orange-500/10", active: "border-orange-300 bg-orange-500/40" },
];

/**
 * Compact voting widget that renders as an overlay panel INSIDE
 * `SorteoLiveModal` (floats in front of the TikTok box during voting).
 *
 * Fetches the active sorteo, shows a mini conic-gradient wheel, the 3 power
 * vote buttons, a live countdown and live vote counts (via SSE).
 *
 * If the user is not authenticated, shows a "log in to vote" hint instead.
 */
export const SorteoLiveVotingPanel = ({ sorteoSummary, secondsLeft }) => {
  const { token, isUser } = useAuth();
  const [sorteo, setSorteo] = useState(null);
  const [voting, setVoting] = useState(false);
  const [loading, setLoading] = useState(true);
  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const load = useCallback(async () => {
    if (!isUser) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/sorteo/active`, auth);
      setSorteo(data.sorteo || null);
    } catch { /* ignore */ }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUser, token]);

  useEffect(() => { load(); }, [load]);

  // Realtime: live vote counts
  useRealtimeEvents({
    sorteo_update: (data) => {
      const kind = data?.kind;
      if (kind === "vote") {
        setSorteo((s) => {
          if (!s) return s;
          return {
            ...s,
            power_votes: data.power_votes || s.power_votes,
            voters_count: typeof data.voters_count === "number" ? data.voters_count : s.voters_count,
          };
        });
        return;
      }
      load();
    },
  });

  const vote = async (power) => {
    if (!sorteo || sorteo.status !== "voting" || !token) return;
    setVoting(true);
    try {
      const { data } = await axios.post(`${API}/sorteo/${sorteo.id}/vote`, { power }, auth);
      setSorteo((s) => s ? {
        ...s,
        my_vote: power,
        power_votes: data?.power_votes || s.power_votes,
        voters_count: typeof data?.voters_count === "number" ? data.voters_count : s.voters_count,
      } : s);
      toast.success(`🗳️ Voto registrado: ${POWERS.find(p => p.id === power)?.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error votando");
    } finally {
      setVoting(false);
    }
  };

  // Compute mini-wheel slices
  const total = (sorteo?.candidates || []).length;
  const sliceDeg = total ? 360 / total : 0;
  const votes = sorteo?.power_votes || {};
  const totalVotes = Object.values(votes).reduce((a, b) => a + b, 0);

  // Compose conic gradient background for the mini wheel
  const wheelBg = total
    ? `conic-gradient(${(sorteo?.candidates || [])
        .map((c, i) => `${c.color} ${i * sliceDeg}deg ${(i + 1) * sliceDeg}deg`)
        .join(",")})`
    : "#222";

  return (
    <motion.div
      data-testid="sorteo-live-voting-panel"
      initial={{ opacity: 0, scale: 0.85, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.92, y: 12 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-0 z-20 flex items-center justify-center p-3 sm:p-4 pointer-events-none"
    >
      <div
        className="pointer-events-auto w-full max-w-[360px] rounded-2xl border border-[#d4af37]/60 shadow-[0_0_60px_rgba(212,175,55,0.45)] p-4 bg-black/85 backdrop-blur-md"
        style={{
          background:
            "linear-gradient(180deg, rgba(20,8,40,0.92) 0%, rgba(5,3,12,0.94) 100%)",
        }}
      >
        {/* Header */}
        <div className="text-center mb-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-bold mb-1">
            ✓ Votación abierta
          </div>
          <h4 className="font-serif text-sm text-white truncate">
            {sorteoSummary?.title || sorteo?.title || "Sorteo en curso"}
          </h4>
          {secondsLeft != null && (
            <div className="mt-1 inline-flex items-center gap-1 text-xs text-[#d4af37] font-bold">
              ⏱ {Math.floor(secondsLeft / 60).toString().padStart(2, "0")}:
              {(secondsLeft % 60).toString().padStart(2, "0")}
            </div>
          )}
        </div>

        {/* Mini wheel */}
        <div className="relative w-32 h-32 mx-auto mb-3">
          <div className="absolute left-1/2 -translate-x-1/2 -top-1.5 z-10 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[14px] border-l-transparent border-r-transparent border-b-[#d4af37]" />
          <div
            className="w-full h-full rounded-full border-2 border-[#d4af37] shadow-[0_0_25px_rgba(212,175,55,0.5)] relative overflow-hidden"
            style={{ background: wheelBg }}
            data-testid="sorteo-live-mini-wheel"
          >
            {(sorteo?.candidates || []).map((c, i) => {
              const angle = i * sliceDeg + sliceDeg / 2;
              return (
                <div
                  key={c.id}
                  className="absolute left-1/2 top-1/2 origin-left text-black text-[8px] font-bold uppercase tracking-wide whitespace-nowrap"
                  style={{ transform: `rotate(${angle}deg) translateX(22px)` }}
                >
                  {c.name.slice(0, 8)}
                </div>
              );
            })}
          </div>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-8 h-8 rounded-full bg-black border-2 border-[#d4af37] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#d4af37]" />
            </div>
          </div>
        </div>

        {/* Status / not-logged-in fallback */}
        {loading ? (
          <div className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin text-[#d4af37] mx-auto" /></div>
        ) : !isUser ? (
          <div className="text-center text-[11px] text-amber-300/80 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-2">
            Inicia sesión para votar
          </div>
        ) : !sorteo ? (
          <div className="text-center text-[11px] text-neutral-400 italic">
            Cargando ruleta…
          </div>
        ) : (
          <>
            <div className="text-center text-[10px] uppercase tracking-widest text-neutral-300 mb-2">
              Vota el <span className="text-[#d4af37]">poder del giro</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {POWERS.map((p) => {
                const Icon = p.icon;
                const count = votes[p.id] || 0;
                const pct = totalVotes ? Math.round((count * 100) / totalVotes) : 0;
                const isMine = sorteo.my_vote === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`live-vote-${p.id}`}
                    disabled={voting}
                    onClick={() => vote(p.id)}
                    className={`p-2 rounded-lg border transition-all ${isMine ? p.active : p.bg} hover:scale-[1.05] disabled:opacity-50`}
                  >
                    <Icon className={`w-4 h-4 mx-auto mb-0.5 ${p.color}`} />
                    <div className="text-[9px] uppercase tracking-widest text-white">{p.label}</div>
                    <div className="text-[9px] text-neutral-300 mt-0.5">{count} · {pct}%</div>
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-center text-neutral-500 mt-2">
              Tu voto: {sorteo.my_vote
                ? <span className="text-[#d4af37] uppercase font-semibold">{POWERS.find((p) => p.id === sorteo.my_vote)?.label}</span>
                : "todavía no votas"}
              <span className="ml-2 text-neutral-600">· {sorteo.voters_count ?? 0} votantes</span>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};
